export function minutesToTimeOfDayString(minutes: number): string
{
	const h = Math.floor(minutes / 60);
	const m = Math.floor(minutes - h * 60);

	return h + '.' + (m < 10 ? '0' + m : m);
}

export function timeOfDayStringToMinutes(input: string): number
{
	const parts = input.split('.', 2);

	let hours = 0, minutes = 0;
		
	const hoursTrimmed = parts[0].trim();

	if (hoursTrimmed.length > 0)
	{
		hours = Number(hoursTrimmed);

		if (isNaN(hours) || hours > 24)
			hours = -1;
	}
	else // This ensures an empty string will return -1
		hours = -1;

	// If hours are valid, parse minutes
	if (hours >= 0 && parts.length >= 2)
	{
		const minutesTrimmed = parts[1].trim();
		minutes = Number(minutesTrimmed);

		if (isNaN(minutes) || minutes >= 60 || (hours === 24 && minutes > 0))
			minutes = -1;
	}

	// Return result if both hours and minutes are valid
	if (hours >= 0 && minutes >= 0)
		return hours * 60 + minutes;

	return -1;
}

/**
 * Check whether an object has properties of the specified type
 */
export function hasPropertiesOfType(obj: any, type: string, properties: string[]): boolean
{
	for (const p of properties)
		if (obj.hasOwnProperty(p) === false || typeof obj[p] !== type)
			return false;

	return true;
}

/**
 * Check whether an object has properties defined / not set to undefined
 */
export function hasProperties(obj: any, properties: string[]): boolean
{
	for (const p of properties)
		if (typeof obj[p] === 'undefined')
			return false;

	return true;
}

/**
 * Try to copy a property of specified primitive type from any-type object to strongly typed object
 */
export function tryCopyProperty<T>(from: any, to: T, prop: keyof T, type: string): boolean
{
	if (typeof from[prop] === type)
	{
		to[prop] = from[prop];
		return true;
	}
	else
		return false;
}

export function tryCopyPropertyString<T>(from: any, to: T, prop: keyof T): boolean
{
	return tryCopyProperty(from, to, prop, 'string');
}

export function tryCopyPropertyNumber<T>(from: any, to: T, prop: keyof T): boolean
{
	return tryCopyProperty(from, to, prop, 'number');
}

export function tryCopyPropertyBool<T>(from: any, to: T, prop: keyof T): boolean
{
	return tryCopyProperty(from, to, prop, 'boolean');
}

/**
 * Get a random integer in the range min.. max.
 */
export function getRandomInt(min: number, max: number): number
{
	min = Math.ceil(min);
	max = Math.floor(max);
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Return an array containing a range of numbers based on parameters.
 * @param count Defines how many values are in the returned range.
 * @param first Defines the first value in the range, default: 0.
 * @param step Defines the step between each value in the range, default: 1.
 */
export function range(count: number, first?: number, step?: number): number[]
{
	const result = new Array<number>(count);

	let _first = 0;
	if (first !== undefined)
		_first = first;

	let _step = 1;
	if (step !== undefined)
		_step = step;

	for (let i = 0; i < count; ++i)
		result[i] = _first + i * _step;

	return result;
}

/**
 * Return the input string with the first letter converted to upper-case
 * @param input The input string of which to capitalize the first letter
 */
export function capitalizeFirstLetter(input: string): string
{
	return input.replace(/^\w/, c => c.toUpperCase());
}

/**
 * Check if the input string is a valid email address by testing it against regular expression.
 * @param input The input string to be tested
 */
export function isValidEmailAddress(input: string): boolean
{
	let regex = /\S+@\S+\.\S+/;
	return regex.test(input);
}

/**
 * Check if the input string is a valid telephone number by testing it against regular expression.
 * @param input The input string to be tested
 */
export function isValidTelephoneNumber(input: string): boolean
{
	let regex = /^[+]?[(]?[0-9]{3}[)]?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4,6}$/im;
	return regex.test(input);
}

/**
 * Replace placeholders in a format string.
 * @param format Format string that contains placeholders, for example {0}, {1}...
 * @param params String values with which the placeholders are replaced
 */
export function formatString(format: string, ...params: string[]): string
{
	let i = 0;
	for (let p of params)
	{
		if (p !== undefined && p !== null)
			format = format.replace('{' + i + '}', p);

		++i;
	}

	return format;
}

/**
 * Check if the given id is a valid legal id
 * @param id
 */
export function isValidLegalId(id: string): boolean
{
	let regex = /^([0-9]{7}-[0-9])/;
	return regex.test(id);
}